import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { VERTICAL_BY_SLUG, VERTICALS } from "@/data/verticals";
import { ArrowUpRight, Check } from "@phosphor-icons/react";
import CTAStrip from "@/components/CTAStrip";
import { Reveal } from "@/components/Reveal";
import PageHead from "@/components/PageHead";

const PRODUCT_TYPES = [
  {
    n: "01",
    t: "Data leads",
    d: "Form-fill records delivered to your CRM in real time, exclusively or shared 2–3 ways.",
  },
  {
    n: "02",
    t: "Live calls",
    d: "Prospects routed to your call center within seconds of expressing intent.",
  },
  {
    n: "03",
    t: "Hot transfers",
    d: "Pre-qualified by our in-house agents, then warm-handed to your licensed team.",
  },
  {
    n: "04",
    t: "Appointment-set",
    d: "Calendar-booked consultations for trades and field sales operations.",
  },
];

export default function VerticalPage() {
  const { slug } = useParams();
  const v = VERTICAL_BY_SLUG[slug];

  if (!v) return <Navigate to="/" replace />;

  const otherVerticals = VERTICALS.filter((x) => x.slug !== v.slug).slice(0, 4);

  return (
    <main data-testid={`vertical-page-${v.slug}`}>
      <PageHead
        title={`${v.name} leads`}
        description={`${v.tagline} ${v.description}`}
        path={`/verticals/${v.slug}`}
        image={v.heroImage}
      />
      {/* HERO */}
      <section className="relative border-b border-citirex-border bg-citirex-bg">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-stretch gap-10 px-6 pb-16 pt-12 md:grid-cols-12 md:gap-14 md:px-12 md:pb-24 md:pt-20">
          <div className="md:col-span-7 md:py-6">
            <Reveal>
              <div className="flex items-center gap-3">
                <Link
                  to="/"
                  className="font-body text-xs uppercase tracking-[0.25em] text-citirex-sub hover:text-citirex-ink"
                >
                  Citirex
                </Link>
                <span className="font-body text-xs text-citirex-sub">/</span>
                <span className="font-body text-xs uppercase tracking-[0.25em] text-citirex-sub">
                  Verticals
                </span>
                <span className="font-body text-xs text-citirex-sub">/</span>
                <span className="font-body text-xs uppercase tracking-[0.25em] text-citirex-accent">
                  {v.short}
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="mt-8 font-editorial text-6xl italic text-citirex-sub md:text-8xl">
                {v.index}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-2 font-display text-5xl font-semibold leading-[0.95] tracking-tighter text-citirex-ink md:text-7xl lg:text-[96px]">
                {v.name}
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-xl font-editorial text-2xl italic leading-snug text-citirex-ink md:text-3xl">
                {v.tagline}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-citirex-sub md:text-lg">
                {v.description}
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid={`vertical-${v.slug}-cta-primary`}
                  className="group inline-flex items-center gap-2 bg-citirex-ink px-7 py-5 font-body text-sm font-semibold uppercase tracking-wider text-citirex-inv transition-colors hover:bg-citirex-accent"
                >
                  Buy {v.short.toLowerCase()} leads
                  <ArrowUpRight
                    size={18}
                    weight="bold"
                    className="transition-transform group-hover:rotate-45"
                  />
                </Link>
                <Link
                  to="/how-it-works"
                  className="font-body text-sm font-semibold uppercase tracking-wider text-citirex-ink underline-offset-4 hover:text-citirex-accent hover:underline"
                >
                  How we generate them →
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.2}>
              <div className="relative aspect-[4/5] w-full overflow-hidden">
                <img
                  src={v.heroImage}
                  alt={v.name}
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-citirex-ink/10" />
                <div className="absolute bottom-6 left-6 right-6 border border-white/20 bg-citirex-ink/60 p-5 backdrop-blur-md">
                  <p className="font-body text-[10px] uppercase tracking-[0.2em] text-citirex-inv/70">
                    Vertical · {v.short}
                  </p>
                  <p className="mt-2 font-editorial text-2xl italic text-citirex-inv md:text-3xl">
                    {v.stats[0].value}
                  </p>
                  <p className="mt-1 font-body text-xs text-citirex-inv/70">
                    {v.stats[0].label}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-citirex-bg2" data-testid={`vertical-${v.slug}-stats`}>
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28">
          <p className="overline">Volume & quality</p>
          <div className="mt-10 grid grid-cols-2 gap-px bg-citirex-border lg:grid-cols-4">
            {v.stats.map((s) => (
              <div key={s.label} className="bg-citirex-bg2 p-6 md:p-8">
                <p className="font-editorial text-5xl font-medium leading-none text-citirex-ink md:text-7xl">
                  {s.value}
                </p>
                <p className="mt-6 font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT TYPES */}
      <section className="border-y border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <p className="overline">Lead products</p>
              <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
                Buy the {v.short.toLowerCase()} traffic that{" "}
                <span className="font-editorial italic">matches your team</span>.
              </h2>
            </div>
            <div className="md:col-span-7">
              <div className="grid grid-cols-1 gap-px bg-citirex-border sm:grid-cols-2">
                {PRODUCT_TYPES.map((p) => (
                  <div key={p.n} className="bg-citirex-bg p-6 md:p-8">
                    <p className="font-editorial text-sm italic text-citirex-sub">
                      {p.n}
                    </p>
                    <h4 className="mt-3 font-display text-xl font-medium text-citirex-ink">
                      {p.t}
                    </h4>
                    <p className="mt-3 font-body text-sm text-citirex-sub">
                      {p.d}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FILTERS / QUALIFIERS */}
      <section className="bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <p className="overline">Filters & qualifiers</p>
              <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
                Built to match your{" "}
                <span className="font-editorial italic">underwriting</span>.
              </h2>
              <p className="mt-6 font-body text-base text-citirex-sub md:text-lg">
                Tell us your appetite. We deliver only what fits — and back it
                with sub-60-second posting, exclusivity controls and full
                consent records.
              </p>
            </div>
            <div className="md:col-span-7">
              <ul className="grid grid-cols-1 gap-px border border-citirex-border sm:grid-cols-2">
                {v.filters.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 border-citirex-border bg-citirex-surface p-5 sm:[&:nth-child(odd)]:border-r"
                  >
                    <Check
                      size={20}
                      weight="bold"
                      className="mt-0.5 shrink-0 text-citirex-accent"
                    />
                    <span className="font-body text-sm text-citirex-ink">
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section className="border-y border-citirex-border bg-citirex-bg2">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <p className="overline">Methodology</p>
          <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-[1.05] md:text-6xl">
            How we generate {v.short.toLowerCase()} demand.
          </h2>
          <div className="mt-16 grid grid-cols-1 gap-px bg-citirex-border md:grid-cols-3">
            {v.methodology.map((m, i) => (
              <div key={i} className="bg-citirex-bg2 p-8">
                <p className="font-editorial text-5xl italic text-citirex-accent">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-6 font-body text-base leading-relaxed text-citirex-ink">
                  {m}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-2">
              <p className="overline">Partner</p>
            </div>
            <div className="md:col-span-10">
              <blockquote className="font-editorial text-3xl italic leading-tight text-citirex-ink md:text-5xl">
                “{v.quote.text}”
              </blockquote>
              <div className="mt-10 flex items-center gap-4">
                <div className="h-12 w-12 bg-citirex-ink" />
                <div>
                  <p className="font-body text-sm font-semibold text-citirex-ink">
                    {v.quote.author}
                  </p>
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                    {v.quote.company}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTAStrip
        eyebrow={`${v.short} · Ready when you are`}
        title={`Open a ${v.short.toLowerCase()} media buy with Citirex.`}
        buttonText="Talk to sales"
      />

      {/* OTHER VERTICALS */}
      <section className="border-t border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <div className="flex items-end justify-between">
            <div>
              <p className="overline">More verticals</p>
              <h2 className="mt-4 font-display text-3xl font-medium md:text-5xl">
                Explore the rest of the book.
              </h2>
            </div>
            <Link
              to="/"
              className="hidden font-body text-sm font-semibold uppercase tracking-wider text-citirex-ink hover:text-citirex-accent md:inline-flex"
            >
              All verticals →
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-px bg-citirex-border md:grid-cols-2 lg:grid-cols-4">
            {otherVerticals.map((o) => (
              <Link
                key={o.slug}
                to={`/verticals/${o.slug}`}
                data-testid={`vertical-${v.slug}-other-${o.slug}`}
                className="group flex flex-col justify-between bg-citirex-bg p-8 transition-colors hover:bg-citirex-surface"
              >
                <div className="flex items-center justify-between">
                  <span className="font-editorial text-sm italic text-citirex-sub">
                    {o.index}
                  </span>
                  <ArrowUpRight
                    size={18}
                    weight="bold"
                    className="transition-transform group-hover:rotate-45"
                  />
                </div>
                <div className="mt-16">
                  <h3 className="font-display text-2xl font-medium text-citirex-ink">
                    {o.name}
                  </h3>
                  <p className="mt-2 font-body text-sm text-citirex-sub">
                    {o.tagline}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
